import SERVICE_REGISTRY from "../services/registry";
import { SETTINGS } from "../settings";

function getElement(selector, type = "data-id") {
    switch (type) {
        case "data-id":
            return document.querySelector(`[data-id="${selector}"]`);
        case "class":
            return document.querySelector(`.${selector}`);
        case "custom":
            return document.querySelector(selector);
        default:
            return document.querySelector(`[data-id="${selector}"]`);
    }
}

function reinitIconsSafely() {
    if (typeof window.reinitIcons === "function") {
        window.reinitIcons();
    } else {
        setTimeout(() => {
            if (typeof window.reinitIcons === "function") {
                window.reinitIcons();
            }
        }, 100);
    }
}

function addClasses(element, ...classes) {
    if (element) element.classList.add(...classes);
}

function removeClasses(element, ...classes) {
    if (element) element.classList.remove(...classes);
}

function addClickListener(selector, callback, context = document) {
    const element = typeof selector === "string" ? context.querySelector(selector) : selector;
    if (element) element.addEventListener("click", callback);
}

function addKeyboardListener(element, callback) {
    if (!element) return;

    element.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            callback();
        }
    });
}

function handleActionWithFocus(element, actionCallback) {
    actionCallback();

    const focusTarget = element.getAttribute("data-focus-after");
    if (!focusTarget) return;

    if (focusTarget === "card-detail-link") {
        setTimeout(() => {
            const targetCard = lastClickedCard || getElement("selected-poi-card");
            const detailLink = targetCard?.querySelector('[data-id="card-detail-link"]');
            detailLink?.focus();
        }, 100);
        return;
    }

    if (focusTarget === "poi-detail-directions") {
        setTimeout(() => {
            const context = element.getAttribute("data-context");
            const contextContainer = getElement(context);
            const poiDetail = contextContainer?.querySelector('[data-id="poi-detail"]');
            if (poiDetail && !poiDetail.classList.contains("wm-hidden")) {
                const directionsButton = poiDetail.querySelector('[data-id="poi-detail-directions"]');
                directionsButton?.focus();
            }
        }, 100);
        return;
    }

    if (focusTarget === "poi-detail-footer-button") {
        setTimeout(() => {
            const context = element.getAttribute("data-context");
            const contextContainer = getElement(context);
            const poiDetail = contextContainer?.querySelector('[data-id="poi-detail"]');
            if (poiDetail && !poiDetail.classList.contains("wm-hidden")) {
                const footerButton = poiDetail.querySelector('[data-id="poi-detail-footer-button"]');
                footerButton?.focus();
            }
        }, 150);
        return;
    }

    const targetElement = getElement(focusTarget);
    if (targetElement) {
        // Skip focusing a text field that is already filled (e.g. the directions origin
        // pre-filled from the route flow) — there is nothing left for the user to type.
        const isFilledInput = typeof targetElement.value === "string" && targetElement.value.trim().length > 0;
        if (isFilledInput) return;
        const delay = focusTarget === "first-demo-poi" ? 150 : 100;
        setTimeout(() => targetElement.focus(), delay);
    }
}

function handleBlockToggle(trigger, customCallback = null) {
    const openTarget = trigger.getAttribute("data-open");
    const closeTarget = trigger.getAttribute("data-close");
    const selfClose = trigger.hasAttribute("data-self-close");

    if (selfClose) {
        getElement(trigger.getAttribute("data-id"))?.classList.add("wm-hidden");
    }

    if (customCallback) {
        customCallback(openTarget, closeTarget);
        updatePanelModes();
        return;
    }

    if (openTarget) {
        openTarget.split(" ").forEach((target) => {
            const element = getElement(target);
            if (element) {
                element.classList.remove("wm-hidden");

                if (target === "directions") {
                    BlockManager.resetDirectionsState();
                }
            }
        });
    }

    if (closeTarget) {
        closeTarget.split(" ").forEach((target) => {
            getElement(target)?.classList.add("wm-hidden");
            getElement(target)?.style.removeProperty("display");
        });
    }

    updatePanelModes();
}

function getButtons(buttonType, context = "", activeOnly = false) {
    const selector = Selectors.BUTTONS[buttonType.toUpperCase()];
    if (!selector) return document.querySelectorAll("");

    const finalSelector = activeOnly ? `${selector}.wm-active` : selector;

    if (context && typeof context === "string") {
        return document.querySelectorAll(`${context} ${finalSelector}`);
    } else if (context && context.nodeType) {
        return context.querySelectorAll(finalSelector);
    } else {
        return document.querySelectorAll(finalSelector);
    }
}

const Selectors = {
    BUTTONS: {
        PILL: ".wm-btn--pill",
        FILTER: '[data-id="filter-button"]',
        ICON: ".wm-btn--icon",
        MAIN: ".wm-btn--main",
    },
    SECTIONS: {
        FILTERS_FAV: ".wm-filters-fav",
        ROUTE_AVOID:'[data-id="route-avoid"]',
        ROUTE_SCHEDULE: ".wm-results--schedule",
    },
};

let lastClickedCard = null;
let POIData = null;

function getPOIById(poiId) {
    return (POIData && POIData[poiId]) || null;
}

function getPOICount(context) {
    if (!POIData) return 0;

    return generatePOIIdsForContext(context).length;
}

const POIConfig = {
    REQUIRED_FIELDS: ["name", "type", "address", "image", "distance"],

    OPTIONAL_FIELDS: ["hours", "services", "prices", "status", "coordinates", "duration"],

    DISTANCE_LIMITS: {
        "route-results": 50,
        "poi-list-results": 100,
        nearby: 5,
        extended: 100,
    },

    AVAILABLE_SERVICES: ["fuel", "carWash", "shop", "restaurant", "atm"],

    _cache: new Map(),
};

function analyzePOIProperties() {
    if (!POIData) return {};

    const cacheKey = "poi-analysis";
    if (POIConfig._cache.has(cacheKey)) {
        return POIConfig._cache.get(cacheKey);
    }

    const properties = {
        totalPOIs: Object.keys(POIData).length,
        validPOIs: 0,
        requiredFields: new Set(),
        optionalFields: new Set(),
        services: new Set(),
        priceTypes: new Set(),
        distanceRange: { min: Number.POSITIVE_INFINITY, max: 0 },
        statusTypes: new Set(),
    };

    Object.entries(POIData).forEach(([poiId, poi]) => {
        if (!poi) return;

        const hasRequiredFields = POIConfig.REQUIRED_FIELDS.every((field) => poi[field]);
        if (hasRequiredFields) {
            properties.validPOIs++;
        }

        POIConfig.REQUIRED_FIELDS.forEach((field) => {
            if (poi[field]) properties.requiredFields.add(field);
        });

        POIConfig.OPTIONAL_FIELDS.forEach((field) => {
            if (poi[field]) properties.optionalFields.add(field);
        });

        if (poi.services && typeof poi.services === "object") {
            Object.keys(poi.services).forEach((service) => {
                properties.services.add(service);
            });
        }

        if (Array.isArray(poi.prices)) {
            poi.prices.forEach((price) => {
                if (price?.name) {
                    properties.priceTypes.add(price.name);
                }
            });
        }

        if (poi.status) {
            properties.statusTypes.add(poi.status);
        }

        if (poi.distance) {
            const distance = parseDistance(poi.distance);
            if (distance !== null) {
                properties.distanceRange.min = Math.min(properties.distanceRange.min, distance);
                properties.distanceRange.max = Math.max(properties.distanceRange.max, distance);
            }
        }
    });

    const result = {
        ...properties,
        requiredFields: Array.from(properties.requiredFields),
        optionalFields: Array.from(properties.optionalFields),
        services: Array.from(properties.services),
        priceTypes: Array.from(properties.priceTypes),
        statusTypes: Array.from(properties.statusTypes),
    };

    POIConfig._cache.set(cacheKey, result);
    return result;
}

function parseDistance(distanceStr) {
    if (!distanceStr || typeof distanceStr !== "string") return null;

    const match = distanceStr.match(/(\d+(?:\.\d+)?)/);
    return match ? Number.parseFloat(match[1]) : null;
}

function parsePrice(priceStr) {
    if (!priceStr || typeof priceStr !== "string") return null;

    const match = priceStr.match(/(\d+(?:\.\d+)?)/);
    return match ? Number.parseFloat(match[1]) : null;
}

function getPOIIdsForContext(context) {
    if (!POIData) return [];

    return generatePOIIdsForContext(context);
}

function generatePOIIdsForContext(context, filters = {}) {
    if (!POIData) return [];

    const cacheKey = `${context}-${JSON.stringify(filters)}`;
    if (POIConfig._cache.has(cacheKey)) {
        return POIConfig._cache.get(cacheKey);
    }

    const filteredPOIs = Object.entries(POIData)
        .filter(([poiId, poi]) => {
            if (!poi) return false;

            const hasRequiredFields = POIConfig.REQUIRED_FIELDS.every((field) => poi[field]);
            if (!hasRequiredFields) return false;

            const contextFilter = getContextFilter(context);
            if (!contextFilter(poi)) return false;

            return applyAdditionalFilters(poi, filters);
        })
        .map(([poiId]) => poiId);

    const sortedPOIs = filteredPOIs.sort((a, b) => {
        const distanceA = parseDistance(POIData[a]?.distance) ?? 999;
        const distanceB = parseDistance(POIData[b]?.distance) ?? 999;
        return distanceA - distanceB;
    });

    POIConfig._cache.set(cacheKey, sortedPOIs);
    return sortedPOIs;
}

function getContextFilter(context) {
    const distanceLimit = POIConfig.DISTANCE_LIMITS[context];

    if (!distanceLimit) {
        return () => true;
    }

    return (poi) => {
        const distance = parseDistance(poi.distance);
        return distance !== null && distance <= distanceLimit;
    };
}

function applyAdditionalFilters(poi, filters) {
    if (!filters || Object.keys(filters).length === 0) return true;

    if (filters.maxDistance) {
        const distance = parseDistance(poi.distance);
        if (distance === null || distance > filters.maxDistance) return false;
    }

    if (filters.services && Array.isArray(filters.services) && filters.services.length > 0) {
        const hasRequiredService = filters.services.some(
            (service) => poi.services && poi.services[service] === "Ouvert",
        );
        if (!hasRequiredService) return false;
    }

    if (filters.priceRange && poi.prices && Array.isArray(poi.prices)) {
        const hasPriceInRange = poi.prices.some((price) => {
            const priceValue = parsePrice(price.price);
            return priceValue !== null && priceValue >= filters.priceRange.min && priceValue <= filters.priceRange.max;
        });
        if (!hasPriceInRange) return false;
    }

    if (filters.status && poi.status !== filters.status) return false;

    if (filters.type && poi.type !== filters.type) return false;

    return true;
}

function invalidatePOICache() {
    POIConfig._cache.clear();
}

function validatePOI(poi) {
    if (!poi || typeof poi !== "object") return false;

    return POIConfig.REQUIRED_FIELDS.every((field) => {
        const value = poi[field];
        return value !== null && value !== undefined && value !== "";
    });
}

export function updatePOIListHeaderTitle(context) {
    const poiListHeader = getElement("poi-list-header");
    if (!poiListHeader) return;

    const headerTitle = poiListHeader.querySelector('[data-id="poi-list-header-title"]');
    if (!headerTitle) return;

    const poiIds = getPOIIdsForContext(context);
    const count = poiIds.length;
    const contextLabels = {
        route: `Stations-service ${total.maps.L10n.ui.common.alongTheRoad}`,
        "route-results": `Stations-service ${total.maps.L10n.ui.common.alongTheRoad}`,
        "poi-list": `${total.maps.L10n.ui.common.nearby_stations}`,
        "poi-list-results": `${total.maps.L10n.ui.common.nearby_stations}`,
        nearby: `${total.maps.L10n.ui.common.nearby_stations}`,
        extended: "Stations-service dans la région",
    };

    const label = contextLabels[context] || `Stations-service disponibles`;
    headerTitle.innerHTML = `<span class="wm-poi-list--count">${count}</span> ${label}`;

    const countText = count === 1 ? "1 station" : `${count} stations`;
    headerTitle.setAttribute("aria-label", `${countText} ${label.toLowerCase()}`);
}


export function initDragScroll(container) {
    if (container.dataset.dragInit) return;
    container.dataset.dragInit = "1";

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;
    const DRAG_THRESHOLD = 5;

    const controller = new AbortController();
    const { signal } = controller;

    container.addEventListener("mousedown", (e) => {
        isDown = true;
        hasDragged = false;
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        container.classList.add("wm-dragging");
    });

    document.addEventListener("mouseup", () => {
        if (!isDown) return;
        isDown = false;
        container.classList.remove("wm-dragging");
    }, { signal });

    document.addEventListener("mousemove", (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = x - startX;
        if (Math.abs(walk) > DRAG_THRESHOLD) {
            hasDragged = true;
        }
        container.scrollLeft = scrollLeft - walk;
    }, { signal });

    container.addEventListener("click", (e) => {
        if (hasDragged) {
            e.stopPropagation();
            e.preventDefault();
        }
    }, true);
}

function renderFavoriteFilters(favoriteFilters) {
    const favoritesContainer = getElement("wm-filters-fav");
    if (!favoritesContainer) return;

    favoritesContainer.innerHTML = "";

    favoriteFilters.forEach((filter, index) => {
        const button = document.createElement("button");
        button.className = "wm-btn--pill";
        button.setAttribute("data-id", "filter-pill");
        button.setAttribute("data-filter-active", "0");
        button.setAttribute("tabindex", (9 + index).toString());
        button.textContent = filter.name;

        favoritesContainer.appendChild(button);
    });

    updateFilterButtonsTabindex();
    initDragScroll(favoritesContainer);
}

function renderDefaultFavoriteFilters() {
    const defaultFilters = StoreConfigManager.getDefaultFavoriteFilters();
    renderFavoriteFilters(defaultFilters);
}

function updateFilterButtonsTabindex() {
    const favoriteFilters = getElement("wm-filters-fav")?.querySelectorAll('[data-id="filter-pill"]') || [];
    const favoriteFilterCount = favoriteFilters.length;

    const filtersButtonTabindex = 9 + favoriteFilterCount;

    const filterButtons = document.querySelectorAll(
        '[data-id="filters-button-desktop"], [data-id="filters-button-mobile"]',
    );
    filterButtons.forEach((button) => {
        button.setAttribute("tabindex", filtersButtonTabindex.toString());
    });
}

export const FilterSystem = {
    activeFilters: new Set(),

    isModalOpen() {
        const modal = getElement("wm-filters-modal");
        return modal && !modal.classList.contains("wm-hidden");
    },

    getActiveFilters() {
        return new Set(this.activeFilters);
    },

    updateUI() {
        this.updateCount();
        this.updateTags();
        this.updateButtons();
        this.updateCloseButton();
        this.updateClearAllTabindex();
        this.updateTagsTabindex();
        FiltersManager.updateCategoryCounters();
    },

    updateCount() {
        const count = this.activeFilters.size;

        for (const btn of document.querySelectorAll(
            '[data-id="filters-button-desktop"], [data-id="filters-button-mobile"]',
        )) {
            btn.setAttribute("data-filter-active", count.toString());
        }

        const clearCount = getElement("clear-count");
        if (clearCount) {
            clearCount.textContent = count.toString();
        }
    },

    updateTags() {
        const container = getElement("filter-tags");
        const section = getElement("selected-filters");
        const clearAllBtn = getElement("clear-all-button");

        if (!container || !section) return;

        const existingTags = Array.from(container.children);
        const existingFilterNames = existingTags
            .map((tag) => tag.querySelector("span")?.textContent?.trim())
            .filter(Boolean);

        for (const filterName of existingFilterNames) {
            if (!this.activeFilters.has(filterName)) {
                const tagToRemove = existingTags.find(
                    (tag) => tag.querySelector("span")?.textContent?.trim() === filterName,
                );
                if (tagToRemove) {
                    container.removeChild(tagToRemove);
                }
            }
        }

        for (const filterName of this.activeFilters) {
            if (!existingFilterNames.includes(filterName)) {
                const tag = this.createFilterTag(filterName);
                container.appendChild(tag);
            }
        }

        if (this.activeFilters.size === 0) {
            section.classList.add("wm-hidden");
        } else {
            section.classList.remove("wm-hidden");
        }

        if (clearAllBtn) {
            if (this.activeFilters.size >= 2) {
                clearAllBtn.classList.remove("wm-hidden");
            } else {
                clearAllBtn.classList.add("wm-hidden");
            }
        }
    },

    createFilterTag(filterName) {
        const tag = document.createElement("span");
        tag.className = "wm-filter-tag";

        const span = document.createElement("span");
        span.textContent = filterName;
        tag.appendChild(span);

        const removeBtn = document.createElement("button");
        removeBtn.className = "wm-filter-tag-remove";
        removeBtn.setAttribute("data-id", "filter-tag-remove");
        removeBtn.setAttribute("aria-label", `Retirer le filtre ${filterName}`);

        removeBtn.innerHTML = '<i data-icon="close"></i>';
        tag.appendChild(removeBtn);

        const removeFilter = () => {
            this.toggleFilter(filterName, false);
            SERVICE_REGISTRY.filterService._removeTagFromCurrentQueryTags(filterName);
        };

        tag.addEventListener("click", removeFilter);

        addKeyboardListener(removeBtn, removeFilter);

        setTimeout(() => {
            reinitIconsSafely();
        }, 10);

        return tag;
    },

    updateButtons() {
        const favoritesContainer = getElement("wm-filters-fav");
        if (favoritesContainer) {
            const filterPills = favoritesContainer.querySelectorAll('[data-id="filter-pill"]');

            for (const btn of filterPills) {
                const name = btn.textContent?.trim();
                const isActive = this.activeFilters.has(name);

                if (isActive) {
                    btn.classList.add("wm-active");
                    btn.setAttribute("data-filter-active", "1");
                } else {
                    btn.classList.remove("wm-active");
                    btn.setAttribute("data-filter-active", "0");
                }
            }
        }

        if (this.isModalOpen()) {
            document.querySelectorAll('[data-id="filter-button"], [data-id="first-filter-button"]').forEach((btn) => {
                const name = btn.querySelector("span")?.textContent?.trim();
                const isActive = this.activeFilters.has(name);

                if (isActive) {
                    btn.classList.add("wm-active");
                    btn.setAttribute("data-filter-active", "1");
                } else {
                    btn.classList.remove("wm-active");
                    btn.setAttribute("data-filter-active", "0");
                }
            });
        }
    },

    updateCloseButton() {
        const closeButton = getElement("close-button");
        const applyButton = getElement("apply-button");

        if (closeButton && applyButton) {
            if (this.activeFilters.size > 0) {
                closeButton.classList.add("wm-hidden");
                applyButton.classList.remove("wm-hidden");
            } else {
                closeButton.classList.remove("wm-hidden");
                applyButton.classList.add("wm-hidden");
            }
        }
    },

    updateClearAllTabindex() {
        const clearAllBtn = getElement("clear-all-button");
        if (clearAllBtn) {
            clearAllBtn.setAttribute("tabindex", "1100");
        }
    },

    updateTagsTabindex() {
        const tagRemoveButtons = document.querySelectorAll('[data-id="filter-tag-remove"]');
        tagRemoveButtons.forEach((button, index) => {
            button.setAttribute("tabindex", (1200 + index).toString());
        });
    },

    toggleFilter(filterName, isFavorite = false) {
        if (this.activeFilters.has(filterName)) {
            this.activeFilters.delete(filterName);
        } else {
            this.activeFilters.add(filterName);
        }

        this.updateUI();
    },

    clearAll() {
        this.activeFilters.clear();
        this.updateUI();
    },

    init() {
        this.initEventListeners();
    },

    initEventListeners() {
        document.addEventListener("click", (e) => {
            const favoritesContainer = getElement("wm-filters-fav");
            const favBtn = favoritesContainer ? e.target.closest(".wm-btn--pill") : null;
            if (favBtn && favoritesContainer?.contains(favBtn)) {
                e.preventDefault();
                const filterName = favBtn.textContent?.trim();
                if (filterName) {
                    this.toggleFilter(filterName, true);
                }
                const button = document.querySelector(`button[data-filter-id="${favBtn.dataset.favId}"]`);
                if (button) {
                    const isActive = button.getAttribute('data-filter-active') === '1';
                    button.setAttribute('data-filter-active', isActive ? '0' : '1');
                    button.classList.toggle('wm-active', !isActive);
                    button.click();
                }
            }
        });

        document.addEventListener("click", (e) => {
            const modalBtn = e.target.closest('[data-id="filter-button"], [data-id="first-filter-button"]');
            if (modalBtn && this.isModalOpen()) {
                e.preventDefault();
                const filterName = modalBtn.querySelector("span")?.textContent?.trim();
                if (filterName) {
                    this.toggleFilter(filterName, false);
                }
            }
        });

        const clearAllBtn = getElement("clear-all-button");
        if (clearAllBtn) {
            const handleClearAll = () => {
                this.clearAll();
            };

            clearAllBtn.addEventListener("click", (e) => {
                e.preventDefault();
                handleClearAll();
            });
            addKeyboardListener(clearAllBtn, handleClearAll);
        }

        document.addEventListener("keydown", (e) => {
            const favoritesContainer = getElement("wm-filters-fav");
            const favBtn = favoritesContainer ? e.target.closest(".wm-btn--pill") : null;
            if (favBtn && favoritesContainer?.contains(favBtn) && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                const filterName = favBtn.textContent?.trim();
                if (filterName) {
                    this.toggleFilter(filterName, true);
                }
            }
        });

        document.addEventListener("keydown", (e) => {
            const modalBtn = e.target.closest('[data-id="filter-button"], [data-id="first-filter-button"]');
            if (modalBtn && this.isModalOpen() && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                const filterName = modalBtn.querySelector("span")?.textContent?.trim();
                if (filterName) {
                    this.toggleFilter(filterName, false);
                }
            }
        });
    },
};

export function updatePanelModes() {
    const panel = getElement("wm-panel");
    if (!panel) return;
    const poiListBlock = getElement("wm-poi-list");
    const routeBlock = getElement("route");
    const directionsBlock = getElement("directions");
    const isPOIListVisible = poiListBlock && !poiListBlock.classList.contains("wm-hidden");
    const isRouteVisible = routeBlock && !routeBlock.classList.contains("wm-hidden");
    const isDirectionsVisible = directionsBlock && !directionsBlock.classList.contains("wm-hidden");

    if (isPOIListVisible) {
        panel.classList.add("wm-poi-list-mode");
    } else {
        panel.classList.remove("wm-poi-list-mode");
    }
    if (isRouteVisible) {
        panel.classList.add("wm-route-mode");
    } else {
        panel.classList.remove("wm-route-mode");
    }
    if (isDirectionsVisible) {
        panel.classList.add("wm-directions-mode");
    } else {
        panel.classList.remove("wm-directions-mode");
    }
}

export function setVisibility(elementId, action) {
    const element = getElement(elementId);
    if (!element) {
        console.error(`Element not found: ${elementId}`);
        return false;
    }

    switch (action) {
        case "show":
            element.classList.remove("wm-hidden");
            break;
        case "hide":
            element.classList.add("wm-hidden");
            break;
        case "toggle":
            element.classList.toggle("wm-hidden");
            break;
        default:
            console.error(`Invalid action: ${action}`);
            return false;
    }
    return true;
}

export const BlockManager = {
    showActions(blockType) {
        if (blockType === "directions" || blockType === "route") {
            setVisibility(`${blockType}-results`, "hide");
        } else if (blockType === "poi-list") {
            updatePOIListTabindex();
        }

        if (blockType === "directions") {
            const originBlock = getElement("directions-origin");
            const destinationBlock = getElement("directions-destination");
            if (originBlock) removeClasses(originBlock, "wm-done");
            if (destinationBlock) removeClasses(destinationBlock, "wm-done");
            const detailsButton = getElement("directions-details-button");
            if (detailsButton) {
                detailsButton.classList.add("wm-hidden");
            }
            NavigationManager.updateButtons(blockType);
        } else if (blockType === "route") {
            const headerBlock = getElement("route-header");
            if (headerBlock) headerBlock.classList.remove("wm-done");
            const originBlock = getElement("route-origin");
            const destinationBlock = getElement("route-destination");
            const avoidSection = document.querySelector(".wm-route--avoid");
            const scheduleSection = document.querySelector(".wm-results--schedule");

            [originBlock, destinationBlock, avoidSection, scheduleSection].forEach((element) => {
                removeClasses(element, "wm-done");
            });
            const searchButton = getElement("route-search-button");
            const detailsButton = getElement("route-details-button");
            if (searchButton) {
                searchButton.disabled = true;
                searchButton.classList.remove("wm-hidden");
            }
            if (detailsButton) {
                detailsButton.classList.add("wm-hidden");
            }
            this.resetRouteButtonStates();
            NavigationManager.updateButtons(blockType);
        } else if (blockType === "poi-list") {
            setVisibility("poi-list-results", "hide");
        }
    },

    showResults(blockType, selectedCardIndex = null) {
        if (blockType === "directions") {
            setVisibility("directions", "show");
            setVisibility("directions-results", "show");

            this.handleDirectionsResults();
        } else if (blockType === "route") {
            setVisibility("route", "show");
            setVisibility("wm-poi-list", "show");
            setVisibility("poi-list-header", "show");
            setVisibility("poi-list-results", "show");

            this.handleRouteResults();
            this.handlePOIListResults(selectedCardIndex, "route-results");
        } else if (blockType === "poi-list") {
            setVisibility("wm-poi-list", "show");
            setVisibility("poi-list-header", "show");
            setVisibility("poi-list-results", "show");
            this.handlePOIListResults(selectedCardIndex, "poi-list-results");
        }

        updatePanelModes();
    },

    handleDirectionsResults() {
        const directionsBlock = getElement("directions");
        if (directionsBlock) {
            directionsBlock.classList.add("wm-show-results");
        }

        const headerBlock = getElement("directions-header");
        if (headerBlock) {
            headerBlock.classList.add("wm-done");
            headerBlock.classList.remove("wm-input-filled");
        }

        const destinationBlock = getElement("directions-destination");
        if (destinationBlock) {
            destinationBlock.classList.add("wm-mini");
        }

        const resultsListContainer = getElement("directions-results-list");
        if (resultsListContainer) {
            // resultsListContainer.innerHTML = "";
            // const newResults = generateMultipleDirectionsResults(4);
            // resultsListContainer.innerHTML = newResults;
        }

        setVisibility("search-button", "hide");
        setVisibility("directions-details-button", "show");
        setVisibility("directions-results", "hide");

        NavigationManager.updateButtons("directions");

        this.initDirectionsResultListeners();

        setTimeout(() => {
            reinitIconsSafely();
        }, 50);
    },

    resetDirectionsState() {
        const directionsBlock = getElement("directions");
        const header = getElement("directions-header");
        const input = getElement("directions-input");
        const detailsButton = getElement("directions-details-button");
        const destinationBlock = getElement("directions-destination");
        const actionsBlock = getElement("directions-actions");

        if (directionsBlock) directionsBlock.classList.remove("wm-show-results");
        if (header) header.classList.remove("wm-done", "wm-input-filled");
        // if (input) input.value = "";
        // if (destinationBlock) destinationBlock.classList.remove("wm-mini");
        if (actionsBlock) actionsBlock.classList.remove("wm-bordered");
        if (detailsButton) {
            const expandLabel = detailsButton.querySelector(".wm-btn--label-expand");
            const reduceLabel = detailsButton.querySelector(".wm-btn--label-reduce");
            if (expandLabel) expandLabel.classList.remove("wm-hidden");
            if (reduceLabel) reduceLabel.classList.add("wm-hidden");
        }

        setVisibility("search-button", "show");
        setVisibility("directions-details-button", "hide");
        setVisibility("directions-results", "hide");
        NavigationManager.updateButtons("directions");
    },

    handleRouteResults() {
        const headerBlock = getElement("route-header");
        if (headerBlock) {
            headerBlock.classList.add("wm-done");
            headerBlock.classList.remove("wm-input-filled");
        }

        setVisibility("route-search-button", "hide");
        setVisibility("route-details-button", "show");

        updatePOIListHeaderTitle("route-results");
        NavigationManager.updateButtons("route");
    },

    handlePOIListResults(selectedCardIndex = null, context = "poi-list") {
        updatePOIListHeaderTitle(context);

        const poiListResults = getElement("poi-list-results");
        const directionButtons = poiListResults?.querySelectorAll('[data-id="open-directions"]');
        const poiCards = poiListResults?.querySelectorAll('[data-id="poi-card"]');

        if (directionButtons) {
            directionButtons.forEach((button) => {
                if (context === "route-results") {
                    button.setAttribute("data-close", "route wm-poi-list wm-search");
                } else {
                    button.setAttribute("data-close", "wm-poi-list wm-search");
                }
            });
        }

        const poiCardsContainer = poiListResults?.querySelector('[data-id="poi-cards"]');
        const allCards = poiCardsContainer?.querySelectorAll('[data-id="poi-card"]');

        if (allCards) {
            if (selectedCardIndex !== null) {
                allCards.forEach((card) => {
                    card.classList.add("wm-hidden");
                    card.classList.remove("wm-poi-card--selected");
                });

                const selectedCard = allCards[selectedCardIndex];
                if (selectedCard) {
                    selectedCard.classList.remove("wm-hidden");
                    selectedCard.classList.add("wm-poi-card--selected");
                }

                const poiListBlock = getElement("wm-poi-list");
                if (poiListBlock) {
                    poiListBlock.classList.add("wm-single-card-mode");
                }

                const toggleButton = getElement("poi-list-result-toggle");
                if (toggleButton) {
                    toggleButton.setAttribute("aria-expanded", "true");
                    toggleButton.querySelector(".wm-btn--label-expand")?.classList.add("wm-hidden");
                    toggleButton.querySelector(".wm-btn--label-reduce")?.classList.remove("wm-hidden");
                }
            } else {
                allCards.forEach((card) => {
                    card.classList.add("wm-hidden");
                    card.classList.remove("wm-poi-card--selected");
                });

                const poiListBlock = getElement("wm-poi-list");
                if (poiListBlock) {
                    poiListBlock.classList.remove("wm-single-card-mode");

                    poiListBlock.classList.remove("wm-poi-detail-mode");
                }

                const poiDetailContainer = poiListResults?.querySelector('[data-id="poi-detail"]');
                if (poiCardsContainer && poiDetailContainer) {
                    poiCardsContainer.classList.remove("wm-hidden");
                    poiDetailContainer.classList.add("wm-hidden");
                }

                const toggleButton = getElement("poi-list-result-toggle");
                if (toggleButton) {
                    toggleButton.setAttribute("aria-expanded", "false");
                    toggleButton.querySelector(".wm-btn--label-expand")?.classList.remove("wm-hidden");
                    toggleButton.querySelector(".wm-btn--label-reduce")?.classList.add("wm-hidden");
                }
            }
        }
    },

    switchOriginDestination(blockType) {
        if (blockType === "directions") {
            const originInput = getElement("directions-input");
            const originBlock = getElement("directions-origin");
            const destinationBlock = getElement("directions-destination");
            const headerContainer = getElement("directions-header");
            const switchButton = getElement("directions-switch");

            if (originInput && originBlock && destinationBlock && headerContainer && switchButton) {
                const hasOriginText = originInput.value.trim().length > 0;
                const isAlreadySwitched = originBlock.previousElementSibling === destinationBlock;
                if (hasOriginText && !isAlreadySwitched) {
                    headerContainer.insertBefore(destinationBlock, originBlock);
                    switchButton.classList.add("wm-top");
                    this.swapIcons(originBlock, destinationBlock, true);
                } else {
                    headerContainer.insertBefore(originBlock, destinationBlock);
                    switchButton.classList.remove("wm-top");
                    this.swapIcons(originBlock, destinationBlock, false);
                }
                NavigationManager.updateButtons(blockType);
            }
        } else if (blockType === "route") {
            const originInput = getElement("route-origin-input");
            const destinationInput = getElement("route-destination-input");

            if (originInput && destinationInput) {
                const originValue = originInput.value;
                const destinationValue = destinationInput.value;
                originInput.value = destinationValue;
                destinationInput.value = originValue;
                NavigationManager.updateButtons(blockType);
            }
        }
    },
    swapIcons(originBlock, destinationBlock, isSwapped) {
        const originIcon = originBlock.querySelector("[data-icon]");
        const destinationIcon = destinationBlock.querySelector("[data-icon]");

        if (originIcon && destinationIcon) {
            const originUse = originIcon.querySelector("use");
            const destinationUse = destinationIcon.querySelector("use");
            if (originUse && destinationUse) {
                if (isSwapped) {
                    originUse.setAttribute("href", "#icon-destination");
                    destinationUse.setAttribute("href", "#icon-arrow-right");
                } else {
                    originUse.setAttribute("href", "#icon-arrow-right");
                    destinationUse.setAttribute("href", "#icon-destination");
                }
            }
        }
    },

    resetRouteButtonStates() {
        const avoidButtons = getButtons("PILL", Selectors.SECTIONS.ROUTE_AVOID);
        avoidButtons.forEach((button) => {
            removeClasses(button, "wm-active");
        });
        const scheduleButtons = getButtons("PILL", Selectors.SECTIONS.ROUTE_SCHEDULE);
        scheduleButtons.forEach((button) => {
            removeClasses(button, "wm-active");
        });
    },

    restoreDirectionsOrder() {
        const originBlock = getElement("directions-origin");
        const destinationBlock = getElement("directions-destination");
        const headerContainer = getElement("directions-header");
        const switchButton = getElement("directions-switch");
        if (originBlock && destinationBlock && headerContainer && switchButton) {
            const isCurrentlySwapped = originBlock.previousElementSibling === destinationBlock;
            if (isCurrentlySwapped) {
                headerContainer.insertBefore(originBlock, destinationBlock);
                switchButton.classList.remove("wm-top");
                this.swapIcons(originBlock, destinationBlock, false);
            }
        }
    },

    initDirectionsResultListeners() {
        const resultsListContainer = getElement("directions-results-list");
        if (!resultsListContainer) return;

        const resultElements = resultsListContainer.querySelectorAll(".wm-directions--result");

        resultElements.forEach((resultElement) => {
            const handleResultClick = (event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleDirectionsResultDetails(resultElement);
            };

            const handleResultKeydown = (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleDirectionsResultDetails(resultElement);
                }
            };

            resultElement.addEventListener("click", handleResultClick);
            resultElement.addEventListener("keydown", handleResultKeydown);
        });
    },
};

const POIContextManager = {
    showPOIDetail(poiIndex, context) {
        return;
        const contextContainer = getElement("poi-list-results");
        if (!contextContainer) return;

        const poiCardsContainer = contextContainer.querySelector('[data-id="poi-cards"]');
        lastClickedCard = poiCardsContainer?.querySelector(`[data-poi-index="${poiIndex}"]`);

        if (!lastClickedCard) return;


        const poiDetailContainer = getElement("poi-detail");

        if (poiCardsContainer && poiDetailContainer) {
            poiCardsContainer.classList.add("wm-hidden");
            poiDetailContainer.classList.remove("wm-hidden");

            const poiListBlock = getElement("wm-poi-list");
            if (poiListBlock) {
                poiListBlock.classList.add("wm-poi-detail-mode");
            }

            const poiIds = getPOIIdsForContext(context);
            const poiId = poiIds[poiIndex];
            const poiData = getPOIById(poiId);

            if (poiData) {
                // poiDetailContainer.innerHTML = generatePOIDetail(poiData);
                const footerButton = poiDetailContainer.querySelector('[data-id="poi-detail-footer-button"]');
                if (footerButton) {
                    footerButton.innerHTML = '<i data-icon="back" data-size="24"></i><span>Retour à la liste</span>';
                    footerButton.setAttribute("data-context", context);
                    footerButton.setAttribute("data-poi-index", poiIndex);
                    footerButton.onclick = () =>
                        handleActionWithFocus(footerButton, () => this.returnToPOIList(context));
                }
            }

            if (context === "route-results") {
                setVisibility("route-footer", "hide");
            }

            reinitIconsSafely();
            updatePanelModes();
        }
    },

    returnToPOIList(context) {
        const contextContainer = getElement("poi-list-results");
        if (!contextContainer) return;

        const poiCardsContainer = contextContainer.querySelector('[data-id="poi-cards"]');
        const poiDetailContainer = getElement("poi-detail");

        if (poiCardsContainer && poiDetailContainer) {
            poiCardsContainer.classList.remove("wm-hidden");
            poiDetailContainer.classList.add("wm-hidden");
            const poiListBlock = getElement("wm-poi-list");
            if (poiListBlock) {
                poiListBlock.classList.remove("wm-poi-detail-mode");
            }

            if (context === "route-results") {
                setVisibility("route-footer", "show");
            }

            updatePanelModes();
        }
    },
};

export const FiltersManager = {
    async openModal() {
        setVisibility("wm-filters-modal", "show");
        const slcontainer = getElement("storemap-store-locator", "class");
        if (slcontainer) {
            slcontainer.style.overflow = "hidden";
        }
        FilterSystem.updateUI();
        await this.initAccordion();

        // this.setupFilterButtonsTabindex();
        // FilterSystem.updateUI();
        // this.updateCategoryCounters();

        // this.initModalKeyboardNavigation();

        // setTimeout(() => {
        //     const firstFilterButton = document.querySelector('[data-id="first-filter-button"]');
        //     if (firstFilterButton) {
        //         firstFilterButton.focus();
        //     } else {
        //         const modal = getElement("wm-filters-modal");
        //         if (modal) {
        //             const firstFocusable = modal.querySelector(
        //                 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
        //             );
        //             if (firstFocusable) {
        //                 firstFocusable.focus();
        //             }
        //         }
        //     }
        // }, 100);
    },

    closeModal() {
        setVisibility("wm-filters-modal", "hide");
        const modal = getElement("wm-filters-modal");
        if (modal) {
            if (modal._escapeHandler) {
                document.removeEventListener("keydown", modal._escapeHandler);
                delete modal._escapeHandler;
            }
            if (modal._focusTrapHandler) {
                document.removeEventListener("keydown", modal._focusTrapHandler);
                delete modal._focusTrapHandler;
            }
        }
        document.body.style.overflow = "";
    },

    toggleModal() {
        if (FilterSystem.isModalOpen()) {
            this.closeModal();
        } else {
            this.openModal();
        }
    },

    updateCategoryCounters() {
        const filterList = getElement("wm-modal--filters-list");
        const categoryItems = filterList?.querySelectorAll(".wm-modal--filters-list-item") || [];
        categoryItems.forEach((item) => {
            const categoryName = item.querySelector(".wm-modal--filters-list-item-title span");
            if (!categoryName) return;

            const categoryFilters = item.querySelectorAll('[data-id="filter-button"].wm-active');
            const activeCount = categoryFilters.length;

            categoryName.setAttribute("data-filter-active", activeCount.toString());
        });
    },
    toggleFilter(filterButton) {
        const filterName = filterButton.querySelector("span")?.textContent;
        if (filterName) {
            FilterSystem.toggleFilter(filterName, false);
            setTimeout(() => filterButton.focus(), 10);
        }
    },

    collapseAllFilters() {
        const filterList = getElement("wm-modal--filters-list");
        const filterListItems = filterList?.querySelectorAll(".wm-modal--filters-list-item") || [];
        filterListItems.forEach((item, index) => {
            const sublist = item.querySelector(".wm-modal--filters-list-item-sublist");
            if (sublist) {
                const isOpen = item.classList.contains("wm-open");
                if (isOpen) {
                    item.classList.remove("wm-open");
                    sublist.classList.remove("wm-open");
                    item.setAttribute("aria-expanded", "false");
                }
            }
        });
    },

    async initAccordion() {
        const filterList = getElement("wm-modal--filters-list");
        const filterListItems = filterList?.querySelectorAll(".wm-modal--filters-list-item") || [];
        filterListItems.forEach((item, index) => {
            const sublist = item.querySelector(".wm-modal--filters-list-item-sublist");
            if (sublist) {
                const toggleAccordion = () => {
                    const isOpen = item.classList.contains("wm-open");
                    if (isOpen) {
                        item.classList.remove("wm-open");
                        sublist.classList.remove("wm-open");
                        item.setAttribute("aria-expanded", "false");
                    } else {
                        filterListItems.forEach((otherItem) => {
                            if (otherItem !== item) {
                                otherItem.classList.remove("wm-open");
                                const otherSublist = otherItem.querySelector(".wm-modal--filters-list-item-sublist");
                                if (otherSublist) otherSublist.classList.remove("wm-open");
                                otherItem.setAttribute("aria-expanded", "false");
                            }
                        });
                        item.classList.add("wm-open");
                        sublist.classList.add("wm-open");
                        item.setAttribute("aria-expanded", "true");
                        setTimeout(() => {
                            if (filterList) {
                                const itemRect = item.getBoundingClientRect();
                                const containerRect = filterList.getBoundingClientRect();
                                const currentScroll = filterList.scrollTop;
                                const targetScroll = currentScroll + (itemRect.top - containerRect.top);
                                
                                filterList.scrollTo({
                                    top: targetScroll,
                                    behavior: "smooth"
                                });
                            }
                        }, 300);
                    
                    }

                    this.setupFilterButtonsTabindex();
                    setTimeout(() => reinitIconsSafely(), 10);
                };
                item.removeEventListener("click", item._accordionClickHandler);
                item._accordionClickHandler = (event) => {
                    if (event.target.closest(".wm-btn--filter")) {
                        return;
                    }
                    toggleAccordion();
                };
                item.addEventListener("click", item._accordionClickHandler);
                item.removeEventListener("keydown", item._accordionKeyHandler);
                item._accordionKeyHandler = (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        if (event.target.closest(".wm-btn--filter")) {
                            return;
                        }
                        event.preventDefault();
                        toggleAccordion();
                    }
                };
                item.addEventListener("keydown", item._accordionKeyHandler);
            }
        });
        setTimeout(() => {
            reinitIconsSafely();
        }, 100);
    },

    async generateFilterContent() {
        const filterList = getElement("wm-modal--filters-list");
        if (!filterList) return;

        try {
            const response = await fetch("./filters-data.json");
            const filtersData = await response.json();

            filterList.innerHTML = "";

            const validCategories = Object.keys(filtersData).filter((category) => category !== "categories");
            validCategories.forEach((category, index) => {
                const categoryData = filtersData[category];
                const categoryName = filtersData.categories[category] || category;

                const listItem = document.createElement("li");
                listItem.className = "wm-modal--filters-list-item";
                listItem.setAttribute("role", "button");
                listItem.setAttribute("aria-expanded", index === 0 ? "true" : "false");
                listItem.setAttribute("aria-controls", `accordion-${index}`);

                if (index === 0) {
                    listItem.classList.add("wm-open");
                }

                const sublist = document.createElement("ul");
                sublist.className = "wm-modal--filters-list-item-sublist";
                sublist.id = `accordion-${index}`;
                if (index === 0) {
                    sublist.classList.add("wm-open");
                }

                categoryData.forEach((filter, filterIndex) => {
                    const subItem = document.createElement("li");
                    subItem.className = "wm-modal--filters-list-item-sub";

                    const button = document.createElement("button");
                    button.className = "wm-btn--filter";
                    button.setAttribute("data-id", "filter-button");
                    button.setAttribute("data-filter-id", filter.id);
                    button.setAttribute("data-filter-category", filter.category);

                    if (index === 0 && filterIndex === 0) {
                        button.setAttribute("data-id", "first-filter-button");
                    }

                    const img = document.createElement("img");
                    img.src = filter.image;
                    img.alt = filter.name;

                    const span = document.createElement("span");
                    span.textContent = filter.name;

                    button.appendChild(img);
                    button.appendChild(span);
                    subItem.appendChild(button);
                    sublist.appendChild(subItem);
                });

                const title = document.createElement("div");
                title.className = "wm-modal--filters-list-item-title";

                const titleSpan = document.createElement("span");
                titleSpan.setAttribute("data-filter-active", "0");
                titleSpan.textContent = categoryName;

                const icon = document.createElement("i");
                icon.setAttribute("data-icon", "arrow-down");

                title.appendChild(titleSpan);
                title.appendChild(icon);

                listItem.appendChild(title);
                listItem.appendChild(sublist);
                filterList.appendChild(listItem);
            });
        } catch (error) {
            console.error("Erreur lors du chargement des filtres:", error);
        }
    },

    initModalKeyboardNavigation() {
        const modal = getElement("wm-filters-modal");
        if (!modal) return;

        modal._escapeHandler = (event) => {
            if (event.key === "Escape") {
                const closeButton = getElement("close-button");
                if (closeButton) {
                    handleActionWithFocus(closeButton, () => this.closeModal());
                } else {
                    this.closeModal();
                }
            }
        };
        document.addEventListener("keydown", modal._escapeHandler);

        modal._focusTrapHandler = (event) => {
            if (event.key !== "Tab") return;

            const modalElements = this.getModalFocusableElements(modal);

            if (modalElements.length === 0) return;

            const firstElement = modalElements[0];
            const lastElement = modalElements[modalElements.length - 1];
            const currentElement = document.activeElement;

            const currentIndex = modalElements.indexOf(currentElement);

            if (currentIndex === -1) {
                event.preventDefault();
                firstElement.focus();
                return;
            }

            if (event.shiftKey) {
                if (currentIndex === 0) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (currentIndex === modalElements.length - 1) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.addEventListener("keydown", modal._focusTrapHandler);
    },

    getModalFocusableElements(modal) {
        const selectors = [
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"]):not([disabled])',
        ].join(", ");

        const elements = Array.from(modal.querySelectorAll(selectors)).filter((el) => {
            const style = window.getComputedStyle(el);

            if (style.display === "none" || style.visibility === "hidden") return false;

            if (["use", "svg", "path"].includes(el.tagName)) return false;

            const tabindex = el.getAttribute("tabindex");
            if (tabindex === "-1") return false;

            if (el.getAttribute("data-id") === "filter-tag-remove") {
                return true;
            }

            const modalContent = modal.querySelector(".wm-modal--filters-content");
            if (modalContent && !modalContent.contains(el)) return false;

            if (el.classList.contains("wm-modal--filters-list-item")) {
                return true;
            }

            if (el.classList.contains("wm-btn--filter")) {
                const accordion = el.closest(".wm-modal--filters-list-item");
                return accordion && accordion.classList.contains("wm-open");
            }

            return true;
        });

        elements.sort((a, b) => {
            const tabindexA = a.getAttribute("tabindex");
            const tabindexB = b.getAttribute("tabindex");

            if (!tabindexA && !tabindexB) return 0;
            if (!tabindexA) return -1;
            if (!tabindexB) return 1;

            const numA = Number.parseInt(tabindexA);
            const numB = Number.parseInt(tabindexB);

            return numA - numB;
        });

        return elements;
    },

    setupFilterButtonsTabindex() {
        const filterList = getElement("wm-modal--filters-list");
        const accordions = filterList?.querySelectorAll(".wm-modal--filters-list-item") || [];
        let currentTabindex = 100;

        accordions.forEach((accordion, accordionIndex) => {
            const filterButtons = accordion.querySelectorAll(".wm-btn--filter");
            const isAccordionOpen = accordion.classList.contains("wm-open");

            accordion.setAttribute("tabindex", currentTabindex.toString());
            currentTabindex++;

            filterButtons.forEach((button, buttonIndex) => {
                if (isAccordionOpen) {
                    button.setAttribute("tabindex", currentTabindex.toString());
                    currentTabindex++;
                } else {
                    button.setAttribute("tabindex", "-1");
                }
            });
        });
    },
};

export const NavigationManager = {
    updateButtons(blockType) {
        if (blockType === "directions") {
            const originInput = getElement("directions-input");
            const searchButton = getElement("search-button");
            const locationButton = getElement("directions-location");
            const switchButton = getElement("directions-switch");
            if (originInput && searchButton && locationButton && switchButton) {
                const hasText = originInput.value.trim().length > 0;
                const headerElement = getElement("directions-header");
                if (headerElement) {
                    if (hasText) {
                        headerElement.classList.add("wm-input-filled");
                    } else {
                        headerElement.classList.remove("wm-input-filled");
                    }
                }
                if (hasText) {
                    setVisibility("directions-location", "hide");
                } else {
                    setVisibility("directions-location", "show");
                }
                searchButton.disabled = !hasText;
            }
        } else if (blockType === "route") {
            const originInput = getElement("route-origin-input");
            const destinationInput = getElement("route-destination-input");
            const locationButton = getElement("route-location");
            const switchButton = getElement("route-switch");
            const searchButton = getElement("route-search-button");

            if (originInput && destinationInput && locationButton && switchButton && searchButton) {
                const hasOriginText = originInput.value.trim().length > 0;
                const hasDestinationText = destinationInput.value.trim().length > 0;
                const hasAnyText = hasOriginText || hasDestinationText;
                const hasBothTexts = hasOriginText && hasDestinationText;
                const headerElement = getElement("route-header");
                if (headerElement) {
                    if (hasAnyText) {
                        headerElement.classList.add("wm-input-filled");
                    } else {
                        headerElement.classList.remove("wm-input-filled");
                    }
                }
                if (hasAnyText) {
                    setVisibility("route-location", "hide");
                } else {
                    setVisibility("route-location", "show");
                }
                searchButton.disabled = !hasBothTexts;
            }
        }
    },

    getCurrentLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    this.updateOriginWithCoordinates(latitude, longitude);
                    NavigationManager.updateButtons("directions");
                },
                (error) => {
                    console.error("Geolocation error:", error);
                    this.showLocationError(error);
                },
            );
        } else {
            console.error("Geolocation not supported");
        }
    },

    updateOriginWithCoordinates(latitude, longitude) {
        const originInput = getElement("directions-input");
        if (originInput) {
            const lat = latitude.toFixed(6);
            const lng = longitude.toFixed(6);
            originInput.value = `${lat}, ${lng}`;
            originInput.dispatchEvent(new Event("input"));
        }
    },

    showLocationError(error) {
        const originInput = getElement("directions-input");
        if (originInput) {
            originInput.classList.add("wm-input--error");
            originInput.placeholder = "Geolocation error";
            setTimeout(() => {
                originInput.classList.remove("wm-input--error");
                originInput.placeholder = "Enter a starting address";
            }, 3000);
        }
    },
};

function initEvents() {
    // initBlockButtons();
    initDirectionsButtons();
    initOriginInput();
    initRouteInputs();
    // initSwitchButton();
    initNavigationButtons();
    initSearchFocus();
    initAutocomplete();
}

export function initBlockButtons() {
    initTriggers();
    const closeButton = getElement("close-button");
    if (closeButton) {
        const handleClose = () => {
            handleActionWithFocus(closeButton, () => {
                handleBlockToggle(closeButton);
                FiltersManager.closeModal();
            });
        };

        closeButton.addEventListener("click", handleClose);
        addKeyboardListener(closeButton, handleClose);
    }

    const applyButton = getElement("apply-button");
    if (applyButton) {
        const handleApply = () => {
            handleActionWithFocus(applyButton, () => {
                handleBlockToggle(applyButton);
                FiltersManager.collapseAllFilters();
                FiltersManager.closeModal();
            });
        };

        applyButton.addEventListener("click", handleApply);
        addKeyboardListener(applyButton, handleApply);
    }

    document
        .querySelectorAll('[data-id="filters-button-desktop"], [data-id="filters-button-mobile"]')
        .forEach((button) => {
            const handleFilters = () => {
                handleActionWithFocus(button, () => {
                    handleBlockToggle(button);
                    FiltersManager.openModal();
                });
            };

            button.addEventListener("click", handleFilters);
            addKeyboardListener(button, handleFilters);
        });

    addClickListener('[data-id="route-button"]', (event) => {
        SETTINGS.isSearchAlongTheRouteActive = true;
        SETTINGS.googleDirectionsRequestOptions.provideRouteAlternatives = false;
        handleActionWithFocus(event.target, () => {
            handleBlockToggle(event.target);
        });
    });
    addClickListener('[data-id="route-cancel-button"]', (event) => {
        handleActionWithFocus(event.target, () => {
            handleBlockToggle(event.target);

            const originInput = getElement("route-origin-input");
            const destinationInput = getElement("route-destination-input");
            if (originInput) originInput.value = "";
            if (destinationInput) destinationInput.value = "";
            NavigationManager.updateButtons("route");
        });
    });

    document.addEventListener("click", (event) => {
        if (event.target.closest('[data-id="route-search-button"]')) {
            BlockManager.showResults("route");
        }
    });
    document.addEventListener("click", (event) => {
        if (event.target.closest('[data-id="route-details-button"]')) {
            toggleRouteDetails();
        }
    });
    addClickListener('[data-id="directions-details-button"]', () => toggleDirectionsDetails());

    initRouteOptionButtons();
    const poiListCloseButton = getElement("poi-list-close");
    if (poiListCloseButton) {
        poiListCloseButton.addEventListener("click", (event) => {
            event.stopPropagation();
            setVisibility("wm-poi-list", "hide");
            setVisibility("wm-demo-poi", "hide");
        });
    }

    const poiListButton = getElement("find-poi-nearby");
    if (poiListButton) {
        poiListButton.addEventListener("focus", () => {
            const searchContainer = getElement("wm-search");
            if (searchContainer) {
                removeClasses(searchContainer, "wm-search--focused");
            }
        });
        poiListButton.addEventListener("click", () => {
            handleActionWithFocus(poiListButton, () => {
                handleBlockToggle(poiListButton, (openTarget, closeTarget) => {
                    if (openTarget && openTarget.includes("wm-poi-list")) {
                        BlockManager.showResults("poi-list");
                    }

                    if (openTarget) {
                        openTarget.split(" ").forEach((target) => {
                            if (target !== "wm-poi-list") {
                                const element = getElement(target);
                                if (element) element.classList.remove("wm-hidden");
                            }
                        });
                    }
                    if (closeTarget) {
                        closeTarget.split(" ").forEach((target) => {
                            getElement(target)?.classList.add("wm-hidden");
                        });
                    }
                });
            });
        });
    }
}

function initTriggers() {
    document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-action]");
        if (!trigger) return;
        const action = trigger.getAttribute("data-action");
        if (!action) return;
        event.stopPropagation();
        switch (action) {
            case "show-poi-detail":
                break;
            case "show-poi-detail-contextual":
                const context = trigger.getAttribute("data-context");
                const poiIndex = trigger.getAttribute("data-poi-index");
                handleActionWithFocus(trigger, () => POIContextManager.showPOIDetail(poiIndex, context));
                break;
            case "toggle-poi-list-results":
                togglePOIListResults();
                break;
            case "show-all-poi-cards":
                showAllPOICards();
                break;
            default:
                console.warn(`Unrecognized action: ${action}`);
                break;
        }
    });

    document.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-open], [data-close], [data-self-close]");
        if (!trigger) return;
        event.stopPropagation();
        handleActionWithFocus(trigger, () => {
            handleBlockToggle(trigger);
        });
    });

    document.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const trigger = event.target.closest("[data-action]");
        if (!trigger) return;
        const action = trigger.getAttribute("data-action");
        if (!action) return;
        event.preventDefault();
        event.stopPropagation();
        switch (action) {
            case "show-poi-detail":
                break;
            case "show-poi-detail-contextual":
                const context = trigger.getAttribute("data-context");
                const poiIndex = trigger.getAttribute("data-poi-index");
                handleActionWithFocus(trigger, () => POIContextManager.showPOIDetail(poiIndex, context));
                break;
            case "toggle-poi-list-results":
                togglePOIListResults();
                break;
            case "show-all-poi-cards":
                showAllPOICards();
                break;
            default:
                console.warn(`Unrecognized action: ${action}`);
                break;
        }
    });

    document.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const trigger = event.target.closest("[data-open], [data-close], [data-self-close]");
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        handleActionWithFocus(trigger, () => {
            handleBlockToggle(trigger);
        });
    });
}

function initDirectionsButtons() {
    document.addEventListener("click", (event) => {
        if (event.target.closest('[data-id="search-button"]')) {
            BlockManager.showResults("directions");
        }
    });
    const cancelButton = getElement("cancel-button");
    if (cancelButton) {
        cancelButton.addEventListener("click", (event) => {
            handleActionWithFocus(event.target, () => {
                BlockManager.resetDirectionsState();
                handleBlockToggle(event.target);
            });
        });
    }
    const directionsCloseButton = getElement("directions-close");
    if (directionsCloseButton) {
        directionsCloseButton.addEventListener("click", (event) => {
            handleActionWithFocus(event.target, () => {
                BlockManager.resetDirectionsState();
                handleBlockToggle(event.target);
            });
        });
    }
}

function initOriginInput() {
    const originInput = getElement("directions-input");
    const searchButton = getElement("search-button");

    if (originInput) {
        originInput.addEventListener("input", () => NavigationManager.updateButtons("directions"));
    }
    if (searchButton) {
        searchButton.disabled = true;
    }
}

function initRouteInputs() {
    const originInput = getElement("route-origin-input");
    const destinationInput = getElement("route-destination-input");
    const searchButton = getElement("route-search-button");

    if (originInput) {
        originInput.addEventListener("input", () => NavigationManager.updateButtons("route"));
    }
    if (destinationInput) {
        destinationInput.addEventListener("input", () => NavigationManager.updateButtons("route"));
    }
    if (searchButton) {
        searchButton.disabled = true;
    }
}

export function initRouteOptionButtons(searchOnRoute = false, origindestinationinput) {

    const avoidButtons = getButtons("PILL", Selectors.SECTIONS.ROUTE_AVOID);
    const directionOptions = {
        avoidHighways: false,
        avoidTolls: false,
        avoidFerries: false,
    };
    avoidButtons.forEach((button) => {
        button.addEventListener("click", () => {
            button.classList.toggle("wm-active");
            const option = button.getAttribute('data-id');
            switch (option) {
                case "highways":
                    directionOptions.avoidHighways = button.classList.contains("wm-active");
                    break;
                case "tolls":
                    directionOptions.avoidTolls = button.classList.contains("wm-active");
                    break;
                case "ferries":
                    directionOptions.avoidFerries = button.classList.contains("wm-active");
                    break;
            }
            const { avoidHighways, avoidTolls, avoidFerries } = directionOptions
            searchOnRoute && SERVICE_REGISTRY.itinaryService.updateOptions(avoidHighways, avoidTolls, avoidFerries);
            const originDestination = origindestinationinput?.get("originDestination");
            if (originDestination) {
                origindestinationinput.set("originDestination", {
                    origin: originDestination.origin,
                    destination: originDestination.destination,
                });
            }
        });
    });
}

export function initDirectionsSwitchButton() {
    const directionsSwitchButton = getElement("directions-switch");
    if (directionsSwitchButton) {
        directionsSwitchButton.addEventListener("click", () => {
            BlockManager.switchOriginDestination("directions");
        });
    }
}

export function initRouteSwitchButton() {
    const routeSwitchButton = getElement("route-switch");
    if (routeSwitchButton) {
        routeSwitchButton.addEventListener("click", () => {
            BlockManager.switchOriginDestination("route");
        });
    }
}

function initNavigationButtons() {
    const locationButton = getElement("directions-location");
    if (locationButton) {
        locationButton.addEventListener("click", () => {
            NavigationManager.getCurrentLocation();
            NavigationManager.updateButtons("directions");
        });
    }
}

export function initSearchFocus() {
    const searchInput = getElement("wm-search-input");
    const searchContainer = getElement("wm-search");
    if (searchInput && searchContainer) {
        searchInput.addEventListener("focus", () => {
            addClasses(searchContainer, "wm-search--focused");
            const scrollY = window.scrollY;
            searchInput.focus({ preventScroll: true });
            // Fallback for iOS Safari
            setTimeout(() => window.scrollTo(0, scrollY), 0);
            searchInput.style.opacity = 0;
            setTimeout(() => searchInput.style.opacity = 1);
        });
        document.addEventListener("click", (event) => {
            if (!searchContainer.contains(event.target)) {
                removeClasses(searchContainer, "wm-search--focused");
            }
        });
    }
}

export function toggleRouteDetails() {
    const detailsButton = getElement("route-details-button");
    const routeBlock = detailsButton?.closest(".wm-route");
    const optionsBlock = routeBlock?.querySelector(".wm-results--options");

    if (optionsBlock && detailsButton && routeBlock) {
        const isVisible = !optionsBlock.classList.contains("wm-hidden");
        const expandLabel = detailsButton.querySelector(".wm-btn--label-expand");
        const reduceLabel = detailsButton.querySelector(".wm-btn--label-reduce");

        if (isVisible) {
            optionsBlock.classList.add("wm-hidden");
            routeBlock.classList.remove("wm-display-details");
            if (expandLabel) expandLabel.classList.remove("wm-hidden");
            if (reduceLabel) reduceLabel.classList.add("wm-hidden");
        } else {
            optionsBlock.classList.remove("wm-hidden");
            routeBlock.classList.add("wm-display-details");
            if (expandLabel) expandLabel.classList.add("wm-hidden");
            if (reduceLabel) reduceLabel.classList.remove("wm-hidden");
        }
    }
}

export function toggleDirectionsDetails() {
    const resultsBlock = getElement("directions-results");
    const detailsButton = getElement("directions-details-button");
    const destinationBlock = getElement("directions-destination");
    const actionsBlock = getElement("directions-actions");

    if (resultsBlock && detailsButton) {
        const isVisible = !resultsBlock.classList.contains("wm-hidden");
        const expandLabel = detailsButton.querySelector(".wm-btn--label-expand");
        const reduceLabel = detailsButton.querySelector(".wm-btn--label-reduce");

        if (isVisible) {
            resultsBlock.classList.add("wm-hidden");
            if (destinationBlock) destinationBlock.classList.add("wm-mini");
            if (expandLabel) expandLabel.classList.remove("wm-hidden");
            if (reduceLabel) reduceLabel.classList.add("wm-hidden");
            if (actionsBlock) actionsBlock.classList.remove("wm-bordered");
        } else {
            resultsBlock.classList.remove("wm-hidden");
            if (destinationBlock) destinationBlock.classList.remove("wm-mini");
            if (expandLabel) expandLabel.classList.add("wm-hidden");
            if (reduceLabel) reduceLabel.classList.remove("wm-hidden");
            if (actionsBlock) actionsBlock.classList.add("wm-bordered");
        }
    }
}

function toggleDirectionsResultDetails(resultElement) {
    const detailsId = resultElement.getAttribute("data-open");

    if (!detailsId) return;

    const detailsElement = document.querySelector(`[data-id="${detailsId}"]`);
    const cardToggleLink = resultElement.querySelector(
        ".wm-directions--result-card .wm-directions--result-actions .wm-link-dark",
    );

    if (!detailsElement) return;

    const isVisible = !detailsElement.classList.contains("wm-hidden");

    if (isVisible) {
        detailsElement.classList.add("wm-hidden");
        resultElement.setAttribute("aria-expanded", "false");
        if (cardToggleLink) {
            cardToggleLink.textContent = "+ Voir les détails";
            cardToggleLink.style.display = "";
        }
        resultElement.setAttribute("aria-label", "Afficher les détails de l'itinéraire");
    } else {
        detailsElement.classList.remove("wm-hidden");
        resultElement.setAttribute("aria-expanded", "true");
        if (cardToggleLink) {
            cardToggleLink.style.display = "none";
        }
        resultElement.setAttribute("aria-label", "Masquer les détails de l'itinéraire");
    }
}

export function showAllPOICards() {
    const poiListResults = getElement("poi-list-results");
    const allCards = poiListResults?.querySelectorAll('[data-id="poi-card"]');
    const poiListBlock = getElement("wm-poi-list");

    if (allCards && poiListBlock) {
        allCards.forEach((card) => {
            card.classList.remove("wm-hidden");
            card.classList.remove("wm-poi-card--selected");
        });

        poiListBlock.classList.remove("wm-single-card-mode");

        const toggleButton = getElement("poi-list-result-toggle");
        if (toggleButton) {
            toggleButton.setAttribute("aria-expanded", "true");
            toggleButton.querySelector(".wm-btn--label-expand")?.classList.add("wm-hidden");
            toggleButton.querySelector(".wm-btn--label-reduce")?.classList.remove("wm-hidden");
        }
    }
}

function togglePOIListResults() {
    const poiListResults = getElement("poi-list-results");
    const allCards = poiListResults?.querySelectorAll('[data-id="poi-card"]');
    const poiListBlock = getElement("wm-poi-list");
    const isSingleCardMode = poiListBlock?.classList.contains("wm-single-card-mode");

    const toggleButton = getElement("poi-list-result-toggle");

    if (isSingleCardMode) {
        const selectedCard = poiListResults?.querySelector('[data-id="poi-card"].wm-poi-card--selected');
        if (selectedCard) {
            const isVisible = !selectedCard.classList.contains("wm-hidden");

            if (isVisible) {
                selectedCard.classList.add("wm-hidden");
                if (toggleButton) {
                    toggleButton.setAttribute("aria-expanded", "false");
                    toggleButton.querySelector(".wm-btn--label-expand")?.classList.remove("wm-hidden");
                    toggleButton.querySelector(".wm-btn--label-reduce")?.classList.add("wm-hidden");
                }
            } else {
                selectedCard.classList.remove("wm-hidden");
                if (toggleButton) {
                    toggleButton.setAttribute("aria-expanded", "true");
                    toggleButton.querySelector(".wm-btn--label-expand")?.classList.add("wm-hidden");
                    toggleButton.querySelector(".wm-btn--label-reduce")?.classList.remove("wm-hidden");
                }
            }
        }
    } else {
        const visibleCards = Array.from(allCards).filter((card) => !card.classList.contains("wm-hidden"));

        if (visibleCards.length === 0) {
            allCards.forEach((card) => {
                card.classList.remove("wm-hidden");
                card.classList.remove("wm-poi-card--selected");
            });

            if (toggleButton) {
                toggleButton.setAttribute("aria-expanded", "true");
                toggleButton.querySelector(".wm-btn--label-expand")?.classList.add("wm-hidden");
                toggleButton.querySelector(".wm-btn--label-reduce")?.classList.remove("wm-hidden");
            }
        } else {
            allCards.forEach((card) => {
                card.classList.add("wm-hidden");
                card.classList.remove("wm-poi-card--selected");
            });

            if (toggleButton) {
                toggleButton.setAttribute("aria-expanded", "false");
                toggleButton.querySelector(".wm-btn--label-expand")?.classList.remove("wm-hidden");
                toggleButton.querySelector(".wm-btn--label-reduce")?.classList.add("wm-hidden");
            }
        }
    }

    updatePOIListTabindex();
}

function selectPOICard(cardIndex) {
    const demoPoiContent = getElement("demo-poi-content");
    const demoImages = demoPoiContent?.querySelectorAll("img") || [];

    demoImages.forEach((otherImg) => {
        otherImg.classList.remove("selected");
    });
    if (demoImages[cardIndex]) {
        demoImages[cardIndex].classList.add("selected");
    }

    setVisibility("wm-demo-poi", "show");
    setVisibility("wm-poi-list", "show");

    BlockManager.showResults("poi-list", cardIndex);
    updatePOIListTabindex();

    setTimeout(() => {
        const poiListResults = getElement("poi-list-results");
        const allCards = poiListResults?.querySelectorAll('[data-id="poi-card"]');
        const selectedCard = allCards?.[cardIndex];

        if (selectedCard && !selectedCard.classList.contains("wm-hidden")) {
            const detailLink = selectedCard.querySelector('[data-id="card-detail-link"]');
            if (detailLink) {
                detailLink.focus();
            }
        }
    }, 150);
}

function updateDemoPoiTabindex(forceUpdate = false) {
    const demoPoiContent = getElement("demo-poi-content");
    const demoImages = demoPoiContent?.querySelectorAll("img") || [];
    const poiListBlock = getElement("wm-poi-list");
    if (!demoImages.length) return;
    if (forceUpdate || (poiListBlock && !poiListBlock.classList.contains("wm-hidden"))) {
        demoImages.forEach((img, index) => {
            img.setAttribute("tabindex", (1500 + index).toString());
        });
    } else {
        demoImages.forEach((img) => {
            img.removeAttribute("tabindex");
        });
    }
}

function updatePOIListTabindex() {
    const poiListResults = getElement("poi-list-results");
    const allCards = poiListResults?.querySelectorAll('[data-id="poi-card"]');
    const poiListHeader = getElement("poi-list-header");

    if (!poiListResults || !allCards || !poiListHeader) return;

    const TABINDEX_START = 2000;
    const selectedCard = poiListResults.querySelector('[data-id="poi-card"].wm-poi-card--selected');
    let currentTabindex = TABINDEX_START;

    // Helper function pour éviter la duplication
    const updateCardTabindex = (card, isSelected) => {
        card.removeAttribute("tabindex");

        const detailLink = card.querySelector('[data-id="card-detail-link"]');
        const directionsButton = card.querySelector('[data-id="open-directions"]');

        if (isSelected) {
            if (detailLink) {
                detailLink.setAttribute("tabindex", currentTabindex.toString());
                currentTabindex++;
            }
            if (directionsButton) {
                directionsButton.setAttribute("tabindex", currentTabindex.toString());
                currentTabindex++;
            }
        } else {
            if (detailLink) detailLink.setAttribute("tabindex", "-1");
            if (directionsButton) directionsButton.setAttribute("tabindex", "-1");
        }
    };

    if (selectedCard) {
        // Mode carte sélectionnée
        allCards.forEach((card) => {
            updateCardTabindex(card, card === selectedCard);
        });

        const toggleButton = getElement("poi-list-result-toggle");
        if (toggleButton) {
            toggleButton.setAttribute("tabindex", currentTabindex.toString());
        }
    } else {
        // Mode toutes les cartes
        const toggleButton = getElement("poi-list-result-toggle");
        if (toggleButton) {
            toggleButton.setAttribute("tabindex", currentTabindex.toString());
            currentTabindex++;
        }

        allCards.forEach((card) => {
            updateCardTabindex(card, !card.classList.contains("wm-hidden"));
        });
    }
}

function initDemoPoiObserver() {
    const demoPoi = getElement("wm-demo-poi");
    if (!demoPoi) return;

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "class") {
                const target = mutation.target;
                const isVisible = !target.classList.contains("wm-hidden");
                const wasHidden = mutation.oldValue?.includes("wm-hidden");

                if (isVisible && wasHidden) {
                    setTimeout(() => {
                        updateDemoPoiTabindex(true);
                        initDemoPoiClicks();
                    }, 50);
                }
            }
        });
    });

    observer.observe(demoPoi, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ["class"],
    });
}

function initDemoPoiClicks() {
    const demoPoiContent = getElement("demo-poi-content");
    const demoImages = demoPoiContent?.querySelectorAll("img") || [];
    demoImages.forEach((img, index) => {
        const handleDemoPoi = () => {
            handleActionWithFocus(img, () => selectPOICard(index));
        };

        img.addEventListener("click", handleDemoPoi);
        addKeyboardListener(img, handleDemoPoi);
    });
}

function initAutocomplete() {
    const autocompleteDropdown = getElement("wm-autocomplete");
    const searchContainer = document.querySelector(".wm-search");

    if (autocompleteDropdown) {
        autocompleteDropdown.addEventListener("click", () => {
            removeClasses(searchContainer, "wm-search--focused");
        });
        const autocompleteItems = autocompleteDropdown.querySelectorAll('[data-id="autocomplete-item"]');
        autocompleteItems.forEach((item, index) => {
            const handleAutocomplete = () => {
                handleActionWithFocus(item, () => {
                    setVisibility("wm-demo-poi", "show");
                    setVisibility("wm-poi-list", "show");
                    BlockManager.showResults("poi-list");
                    removeClasses(searchContainer, "wm-search--focused");
                });
            };

            item.addEventListener("click", handleAutocomplete);
            addKeyboardListener(item, handleAutocomplete);
        });
    }
}

function initInputFocusVisibleHandling() {
    let isUsingKeyboard = false;
    document.addEventListener("keydown", (event) => {
        if (event.key === "Tab") {
            isUsingKeyboard = true;
        }
    });
    document.addEventListener("mousedown", () => {
        isUsingKeyboard = false;
    });
    document.addEventListener("mouseup", () => {
        isUsingKeyboard = false;
    });
    document.addEventListener("touchstart", () => {
        isUsingKeyboard = false;
    });
    // Scope to Woosmap's own inputs only. The SL-owned text inputs all carry a
    // `wm-` prefixed class (wm-search--input, wm-route--*, wm-directions--*, wm-hidden-input);
    // host-page inputs do not. 
    const isWoosmapTextInput = (element) =>
        element.tagName === "INPUT" &&
        element.type === "text" &&
        Array.from(element.classList).some((cls) => cls.startsWith("wm-"));

    document.addEventListener("focusin", (event) => {
        const element = event.target;
        if (isWoosmapTextInput(element) && !isUsingKeyboard) {
            element.style.outline = "none";
            element.style.boxShadow = "none";
        }
    });
    document.addEventListener("focusout", (event) => {
        const element = event.target;
        if (isWoosmapTextInput(element)) {
            element.style.outline = "";
            element.style.boxShadow = "";
        }
    });
}

let isInitialized = false;

async function init() {
    if (isInitialized) {
        return;
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init);
    } else {
        initEvents();
        initInputFocusVisibleHandling();
        initDemoPoiObserver();

        // await initializePOIData();
        // await initializeFavoriteFilters();
        updateFilterButtonsTabindex();
        NavigationManager.updateButtons("directions");
        NavigationManager.updateButtons("route");
        setTimeout(() => {
            FiltersManager.setupFilterButtonsTabindex();
        }, 100);
        setTimeout(() => {
            reinitIconsSafely();
        }, 150);

        isInitialized = true;
    }
}

init();

FilterSystem.init();
